import { visit } from "@hotwired/turbo";

// #STYLE_GUIDE: TypeScript interfaces use camelCase (props are transformed by turbo-mount)
interface QuestionData {
  id: string;
  questionText?: string;
}

interface AnswerProps {
  id: string;
  question: QuestionData | string;
  questionId: string;
  answerText: string;
  createdByType: string;
  createdBy: string;
  sourcePassage: string;
  createdAt: string;
  updatedAt: string;
}

interface AnswerShowProps {
  answer: AnswerProps;
}

// #STYLE_GUIDE: Show component displays single record details
// - Uses daisyUI components for consistent styling
// - Navigation via Turbo.visit() for SPA-like experience
export default function AnswerShow({ answer }: AnswerShowProps) {
  const handleBack = () => {
    // Navigate back to the question page
    visit(`/questions/${answer.questionId}`);
  };

  return (
    <div className="space-y-6">
      {/* Header with actions */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Answer Details</h1>
        <button onClick={handleBack} className="btn btn-outline">
          Back to Question
        </button>
      </div>

      {/* Details card */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">
                <span className="label-text font-semibold">ID</span>
              </label>
              <p className="text-base-content">{answer.id}</p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Question</span>
              </label>
              <p className="text-base-content">
                {(() => {
                  // Handle reference field display - show questionText if it's an object, otherwise show the value
                  const value = answer.question;
                  if (
                    typeof value === "object" &&
                    value !== null &&
                    "questionText" in value
                  ) {
                    return value.questionText;
                  }
                  return value || "Not set";
                })()}
              </p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Answer text</span>
              </label>
              <div className="text-base-content whitespace-pre-wrap">
                {answer.answerText || "Not set"}
              </div>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">
                  Created by type
                </span>
              </label>
              <p className="text-base-content">
                {answer.createdByType || "Not set"}
              </p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Created by</span>
              </label>
              <p className="text-base-content">
                {(() => {
                  // Handle reference field display - show displayName if it's an object, otherwise show the value
                  const value = answer.createdBy;
                  if (
                    typeof value === "object" &&
                    value !== null &&
                    "displayName" in value
                  ) {
                    return value.displayName;
                  }
                  return value || "Not set";
                })()}
              </p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Source passage</span>
              </label>
              <div className="text-base-content whitespace-pre-wrap">
                {answer.sourcePassage || "Not set"}
              </div>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Created At</span>
              </label>
              <p className="text-base-content">
                {new Date(answer.createdAt).toLocaleString()}
              </p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Updated At</span>
              </label>
              <p className="text-base-content">
                {new Date(answer.updatedAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
